import { Component, effect, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Severity, SnackbarMessage } from './model/snackbar';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';

import { AutoLogOutService } from './services/autoLogOut.service';
import { DomSanitizer } from '@angular/platform-browser';
import { HeaderComponent } from './components/shared/header/header.component';
import { MaintenanceModeService } from './services/maintenanceMode.service';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { NgxSpinnerService } from 'ngx-spinner';
import { OnlineService } from './services/online.service';
import { NavigationStart, Router } from '@angular/router';
import { SidebarComponent } from './components/shared/sidebar/sidebar.component';
import { SnackBarService } from './services/snackBar.service';
import { SpinnerService } from './services/spinner.service';
import { StringUtils } from './utils/stringUtils';
import { ThemeService } from './services/theme.service';
import { ToastrService } from 'ngx-toastr';
import { VersionCheckService } from './services/version-check.service';
import { environment } from 'src/environments/environment';
import { LoadingService } from './services/loading.service';
import { HeaderClientComponent } from './components/shared/header-client/header-client.component';
import { AppTypeEnum } from './model/appType';
import { CookieService } from 'ngx-cookie-service';
import { ViewPdfService } from './services/view-pdf.service';
import { OnlineSyncService } from './services/onlineSync.service';
import { PdfViewerDialogComponent } from './components/shared/pdf-viewer/pdf-viewer.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  @ViewChild(HeaderComponent, { static: true }) header: HeaderComponent;
  @ViewChild(SidebarComponent, { static: true }) sidebar: SidebarComponent;
  @ViewChild(HeaderComponent, { static: true }) headerClient: HeaderClientComponent;


  defaultElevation = 1;
  raisedElevation = 6;

  isOnline: boolean;
  endTime = 30; // Minutos

  unsubscribe$: Subject<void> = new Subject();
  timerSubscription: Subscription;
  isLoginClient = false;
  loading$: Observable<boolean>;
  private destroy$ = new Subject<void>();

  private iconsToUse = ['excel', 'doc_lock', 'bioindicator'];
  private flagsToUse = ['es', 'gb', 'fr', 'be', 'cat', 'cn', 'de', 'es', 'it', 'jp', 'pt', 'ru', 'vn'];
  constructor(
    private router: Router,
    private themeService: ThemeService,
    private versionCheckService: VersionCheckService,
    private autoLogOutService: AutoLogOutService,
    private snackBarService: SnackBarService,
    private spinnerService: SpinnerService,
    private maintenanceModeService: MaintenanceModeService,
    private onlineService: OnlineService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogRef: MatDialog,
    public loader: LoadingService,
    private cookieService: CookieService,
    private onlineSync: OnlineSyncService,
    private dialog: MatDialog,
    private pdfService: ViewPdfService) {
    
    this.loading$ = this.loader.loading$;

    this.iconsToUse.forEach(item => {
      this.matIconRegistry.addSvgIcon(item, this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/' + item + '.svg'));
    });

    this.flagsToUse.forEach(item => {
      this.matIconRegistry.addSvgIcon(item, this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/flags/' + item + '.svg'));
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.pdfService.clear();
      }
    });

    effect(() => {
      const source = this.pdfService.pdfSource();
      if (source) {
        const name = this.pdfService.pdfName();

        const dialogRef = this.dialog.open(PdfViewerDialogComponent, {
          width: '80%',
          height: '80%',
          data: { source, name }
        });

         dialogRef.afterClosed().subscribe(() => {
          this.pdfService.clear()
         });
      }
    });
  }

  @HostListener('window:beforeunload')
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.autoLogOutService.logOutUser();
  }

  @HostListener('document:keyup', ['$event'])
  @HostListener('document:click', ['$event'])
  @HostListener('document:wheel', ['$event'])
  resetTimerListener(event: Event): void {
    this.autoLogOutService.notifyUserAction();
  }

  updateUnreadNotifications(total: number): void {
    const rol = this.cookieService.get('rol');

    if (rol === 'guest') {
      this.headerClient.unreadNotifications = total;
    } else {
      if (this.header) {
        this.header.unreadNotifications = total;
      }

    }
  }

  ngOnInit(): void {
    this.isLoginClient = false;
    const env: any = environment;
    const versionCheckURL = env.versionCheckURL as string;

    this.versionCheckService.initVersionCheck(versionCheckURL);

    this.resetTimer();
    this.autoLogOutService.userActionOccured.pipe(takeUntil(this.unsubscribe$), takeUntil(this.destroy$)).subscribe(() => {
      if (this.timerSubscription) {
        this.timerSubscription.unsubscribe();
      }
      this.resetTimer();
    });

    this.spinnerService.message$.pipe(takeUntil(this.destroy$)).subscribe((show: boolean) => {
      if (show === true) {
        void this.spinner.show();
      } else {
        void this.spinner.hide();
      }
    });

    this.onlineService.online$.subscribe(online => this.isOnline = online);

    setInterval(() => {
      this.maintenanceModeService.isMaintenanceMode()
        .pipe(catchError((err) => {
          console.error('Error checking maintenance mode', err);
          if (this.isOnline && !this.isDev()) {
            this.dialogRef.closeAll();
            void this.router.navigateByUrl('/maintenance');
          }
          return of(null);
        })
      ).subscribe();
    }, 5 * 60 * 1000);
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      if (data.url) {
       // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
       const rol = this.cookieService.get('rol');
       if (rol === 'guest') {
          if (!data.url?.startsWith('/login-client')) {
            this.isLoginClient = true;
          } else {
            this.isLoginClient = false;
          }
        } else {
          this.isLoginClient = false;
        }
      }
    });
  }

  resetTimer(): void {
    const interval = 1000;
    const duration = this.endTime * 60;
    this.timerSubscription = timer(0, interval).pipe(take(duration), takeUntil(this.destroy$))
      .subscribe(() => {
        // No hay que hacer nada, pero si no se mete esto, se queja Sonar
      }, () => {
        // No hay que hacer nada, pero si no se mete esto, se queja Sonar
      }, () => this.autoLogOutService.logOutUser());
  }

  getTheme(): string {
    const url = this.router.url;
    if (url?.includes('air')) {
      return this.themeService.getThemeHome(AppTypeEnum.AIR);
    } else if (url?.includes('calibrates') || url?.includes('balances') || url?.includes('particles')) {
      return this.themeService.getThemeHome(AppTypeEnum.CALIBRATES);
    } else if (url?.includes('thermal')) {
      return this.themeService.getThemeHome(AppTypeEnum.THERMAL);
    }  else if (url?.includes('qualificates')) {
      return this.themeService.getThemeHome(AppTypeEnum.QUALIFICATES);
    }
  }

  isDev(): boolean {
    return environment.development;
  }

  getThemeIconPath(): string {
    const url = this.router.url;
    return this.themeService.getThemeIconPathSelection(url);
  }

}
